import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./TutorRegistration.css";
import { FaArrowLeft, FaTimes } from "react-icons/fa";
import { useLoadScript } from "@react-google-maps/api";
import Step1 from "../registrationSteps/Step1";
import Step2 from "../registrationSteps/Step2";
import StepCredentials from "../registrationSteps/StepCredentials";
import Step4 from "../registrationSteps/Step4";
import Step5 from "../registrationSteps/Step5";
import AnalysisSection1 from "../analysisSection/AnalysisSection1";
import AnalysisSection2 from "../analysisSection/AnalysisSection2";
import AnalysisSection3 from "../analysisSection/AnalysisSection3";
import AnalysisSection4 from "../analysisSection/AnalysisSection4";
import AnalysisSection5 from "../analysisSection/AnalysisSection5";
import SuccessModal from "../modals/SuccessModal";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { toast } from "react-hot-toast";
import { consumePostAuthRedirect } from "../../utils/postAuthRedirect";
import { findUnusualRateWarnings } from "../../utils/rateThresholds";
import { validateFile } from "../../utils/fileValidation";

const TutorRegistration = () => {
  const navigate = useNavigate();
  const { user, updateUser, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;

  const [formData, setFormData] = useState({
    picture: null,
    coverImage: null,
    idDocument: null,

    // Personal Info
    phone: "",
    gender: "",

    idType: "", // ← add this
    nin: "", // ← add this
    ninVerification: null, // ← add this

    // Education
    schoolName: "",
    degree: "",
    degreeOther: "",
    fieldOfStudy: "",
    fieldOfStudyOther: "",
    additionalSchools: [],

    // ────── NEW STRUCTURE FOR STEP 2 ──────
    selectedCategories: [], // e.g. ["Digital & Technology Skills", ...]
    categoryDetails: {}, // e.g. { "Digital & Technology Skills": { courses: [...], rate: "5000", period: "hour" } }
    teachingModes: [], // subset of ["online", "hybrid", "physical"]
    teachingModeOther: "",

    // Legacy fields (kept temporarily for backward compatibility)
    programType: "",
    mainProgram: "",
    selectedCourses: [],
    ratePeriod: "hour",
    hourlyRate: "",
    currency: "NGN",
    offersIntroSession: false,

    // Others
    location: { address: "", placeId: "", lat: 0, lng: 0 },
    bio: "",
    socialMedia: { platform: "", link: "" },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  // console.log(formData); // check what's missing
  // console.log(errors); // ← this will show you exactly why it blocks

  // ── Autosave ──────────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const isDirtyRef = useRef(false);
  const hasResumedRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  // The small "Saving…/Saved" text at the top is easy to miss entirely —
  // this fires one unmissable toast the first time a save actually
  // succeeds in this session, so the user has clear, explicit confirmation
  // they can close the tab and come back later. Not repeated on every
  // autosave tick after that — that would just be noise.
  const hasShownDraftToastRef = useRef(false);

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.firstName || "Tutor";

  const validateForm = (step) => {
    let tempErrors = {};

    if (step === 1) {
      // A Google/Facebook sign-up already has a profile photo on the account
      // (see socialLogin) — don't force a fresh upload on top of that.
      if (!formData.picture && !user?.picture?.url)
        tempErrors.picture = "Profile picture is required";
      if (!formData.gender) tempErrors.gender = "Gender is required";

      const bioWords = formData.bio?.trim()
        ? formData.bio
            .trim()
            .split(/\s+/)
            .filter((w) => w.length > 0).length
        : 0;
      if (bioWords < 75)
        tempErrors.bio = `Bio must be at least 75 words (currently ${bioWords})`;
      else if (bioWords > 150)
        tempErrors.bio = `Bio must not exceed 150 words (currently ${bioWords})`;

      if (!formData.location?.address?.trim()) {
        tempErrors.address = "Address is required";
      }

      // Must pick an ID type
      if (!formData.idType) {
        tempErrors.idType = "Please select an identity document type";
      }

      // NIN selected but not verified
      // if (formData.idType === "nin") {
      //   const nin = (formData.nin || "").trim();
      //   if (!nin) {
      //     tempErrors.nin = "Please enter your NIN";
      //   } else if (!/^\d{11}$/.test(nin)) {
      //     tempErrors.nin = "NIN must be exactly 11 digits";
      //   } else if (
      //     formData.ninVerification?.status !== "found" &&
      //     formData.ninVerification?.status !== "verified"
      //   ) {
      //     tempErrors.nin = "Please verify your NIN before continuing";
      //   }
      // }

      if (formData.idType === "nin") {
        const nin = (formData.nin || "").trim();
        if (nin && !/^\d{11}$/.test(nin)) {
          tempErrors.nin = "NIN must be exactly 11 digits";
          // Only flag format errors — entering NIN is optional for now
        }
      }

      // ID doc upload always required (for all ID types including NIN)
      if (!formData.id) {
        tempErrors.id =
          formData.idType === "nin"
            ? "Please upload your NIN slip or NIMC card"
            : "Please upload your identity document";
      }
    }

    if (step === 2) {
      // Education
      if (!formData.schoolName?.trim())
        tempErrors.schoolName = "School name is required";

      if (!formData.degree) tempErrors.degree = "Degree is required";

      if (formData.degree === "other" && !formData.degreeOther?.trim())
        tempErrors.degreeOther = "Please specify your degree";

      if (!formData.fieldOfStudy)
        tempErrors.fieldOfStudy = "Field of study is required";

      if (
        formData.fieldOfStudy === "other" &&
        !formData.fieldOfStudyOther?.trim()
      )
        tempErrors.fieldOfStudyOther = "Please specify your field";

      // Teaching information
      if (!formData.programType)
        tempErrors.programType = "Program type is required";

      if (!formData.mainProgram)
        tempErrors.mainProgram = "Course category is required";

      if (
        !Array.isArray(formData.selectedCourses) ||
        formData.selectedCourses.length === 0
      )
        tempErrors.courses = "Select at least one course";

      if (formData.selectedCourses.length > 5)
        tempErrors.courses = "You can select a maximum of 5 courses";

      if (!formData.ratePeriod)
        tempErrors.ratePeriod = "Rate period is required";

      if (
        !formData.hourlyRate ||
        isNaN(formData.hourlyRate) ||
        Number(formData.hourlyRate) <= 0
      )
        tempErrors.hourlyRate = "Enter a valid rate";
    }

    return tempErrors;
  };

  // The only 4 fields that must never be skippable: profile photo, bio, ID
  // card, location. Checked independently of validateForm(1) above because
  // that full check is only ever run from the "Continue" button — both
  // "Skip" (skipStep, below) and the final-submit gate bypassed it entirely,
  // which meant a profile could be marked complete with none of these 4
  // present. Bio reuses the same word-count rule as validateForm so the two
  // don't disagree about what counts as "has a bio."
  const validateCompulsoryBasics = () => {
    const tempErrors = {};

    if (!formData.picture && !user?.picture?.url)
      tempErrors.picture = "Profile picture is required";

    const bioWords = formData.bio?.trim()
      ? formData.bio.trim().split(/\s+/).filter((w) => w.length > 0).length
      : 0;
    if (bioWords < 75)
      tempErrors.bio = `Bio must be at least 75 words (currently ${bioWords})`;
    else if (bioWords > 150)
      tempErrors.bio = `Bio must not exceed 150 words (currently ${bioWords})`;

    if (!formData.location?.address?.trim())
      tempErrors.address = "Location is required";

    if (!formData.id && !user?.documents?.length)
      tempErrors.id = "An ID card upload is required";

    return tempErrors;
  };

  // Shared by the final submit, autosave, and step-transition saves so all
  // three serialize `formData` the exact same way — `includeFiles: false` is
  // used for the periodic debounced save so it doesn't re-upload an unchanged
  // picture/ID document on every autosave tick.
  const buildFormData = ({
    includeFiles = true,
    includeCompletion = false,
    step = currentStep,
  } = {}) => {
    const data = new FormData();

    if (includeFiles) {
      if (formData.picture) data.append("picture", formData.picture);
      if (formData.coverImage) data.append("coverImage", formData.coverImage);
      if (formData.id) data.append("documents", formData.id);
    }

    if (formData.idType) data.append("idType", formData.idType);
    if (formData.nin?.trim()) data.append("nin", formData.nin.trim());
    if (formData.ninVerification) {
      data.append("ninVerification", JSON.stringify(formData.ninVerification));
    }

    data.append("gender", formData.gender || "");
    if (formData.phone?.trim()) data.append("phone", formData.phone.trim());

    data.append("schoolName", formData.schoolName || "");
    data.append("degree", formData.degree || "");
    if (formData.degree === "other")
      data.append("degreeOther", formData.degreeOther || "");
    data.append("fieldOfStudy", formData.fieldOfStudy || "");
    if (formData.fieldOfStudy === "other")
      data.append("fieldOfStudyOther", formData.fieldOfStudyOther || "");

    data.append("bio", formData.bio || "");
    data.append("location[address]", formData.location?.address || "");
    data.append("location[placeId]", formData.location?.placeId || "");
    data.append("location[lat]", formData.location?.lat || 0);
    data.append("location[lng]", formData.location?.lng || 0);

    data.append(
      "selectedCategories",
      JSON.stringify(formData.selectedCategories || []),
    );
    data.append(
      "categoryDetails",
      JSON.stringify(formData.categoryDetails || {}),
    );
    data.append("teachingModes", JSON.stringify(formData.teachingModes || []));
    if (formData.teachingModeOther?.trim()) {
      data.append("teachingModeOther", formData.teachingModeOther.trim());
    }

    data.append("programType", "multiple");
    data.append("currency", "NGN");
    data.append(
      "offersIntroSession",
      formData.offersIntroSession ? "true" : "false",
    );

    if (formData.additionalSchools?.length > 0) {
      data.append(
        "additionalSchools",
        JSON.stringify(formData.additionalSchools),
      );
    }
    if (formData.additionalLicenses?.length > 0) {
      data.append(
        "additionalLicenses",
        JSON.stringify(formData.additionalLicenses),
      );
    }

    data.append("registrationStep", String(step));

    if (includeCompletion) {
      data.append("profileCompleted", "true");
      data.append("role", "tutor");
    }

    return data;
  };

  // Partial save — used by autosave and step transitions. Silently no-ops if
  // nothing has changed since the last save (isDirtyRef), so moving
  // forward/back repeatedly doesn't spam the backend with identical writes.
  const saveProgress = async ({ includeFiles = true, step } = {}) => {
    if (!isDirtyRef.current) return;

    setSaveStatus("saving");
    try {
      const data = buildFormData({ includeFiles, includeCompletion: false, step });
      const res = await api.put("/users/me", data);
      updateUser(res.data.user);
      isDirtyRef.current = false;
      setSaveStatus("saved");

      if (!hasShownDraftToastRef.current) {
        hasShownDraftToastRef.current = true;
        toast.success(
          "Draft saved — you can close this tab and continue anytime. Just log back in.",
          { duration: 5000 },
        );
      }
    } catch (err) {
      console.error("Autosave failed:", err);
      setSaveStatus("error");
    }
  };

  // Resume-on-mount: if this user already has saved progress (registered,
  // left, came back), prefill the form and jump to the saved step instead of
  // starting over. Runs once; gates the autosave-trigger effect below via
  // hasResumedRef so the debounced autosave can't fire — and overwrite the
  // draft with blank initial state — before this has had a chance to run.
  //
  // Deliberately re-fetches via refreshUser() instead of trusting the
  // `user` object already in context: right after signup/OTP-verify,
  // AuthContext's cached user is the MINIMAL shape the login response
  // returns (_id/name/email/role/token) — it has no registrationStep, bio,
  // schoolName, etc. at all. Resuming off that cached object meant the
  // condition below was always false (registrationStep undefined), so nothing
  // ever prefilled even once a real session existed.
  useEffect(() => {
    let cancelled = false;

    const resume = async () => {
      const fresh = await refreshUser().catch(() => null);
      if (cancelled) return;

      const source = fresh || user;

      // registrationStep alone is the WRONG resume signal — it only
      // advances when "Continue" is clicked (see nextStep), but the
      // debounced autosave saves text fields on step 1 the whole time
      // BEFORE that click. A user who types their bio/location/etc. and
      // closes the tab without ever clicking Continue has real saved data
      // with registrationStep still at 1 — gating on ">1" meant that data
      // silently never came back, which is exactly "taken to step1 afresh"
      // even though the save genuinely worked.
      const hasDraftData = Boolean(
        source?.bio?.trim() ||
          source?.phone?.trim() ||
          source?.gender ||
          source?.idType ||
          source?.schoolName?.trim() ||
          source?.degree ||
          source?.fieldOfStudy ||
          source?.location?.address?.trim() ||
          source?.selectedCategories?.length ||
          (source?.categoryDetails && Object.keys(source.categoryDetails).length),
      );

      if (source && !source.profileCompleted && hasDraftData) {
        setFormData((prev) => ({
          ...prev,
          phone: source.phone || prev.phone,
          gender: source.gender || prev.gender,
          idType: source.idType || prev.idType,
          schoolName: source.schoolName || prev.schoolName,
          degree: source.degree || prev.degree,
          degreeOther: source.degreeOther || prev.degreeOther,
          fieldOfStudy: source.fieldOfStudy || prev.fieldOfStudy,
          fieldOfStudyOther: source.fieldOfStudyOther || prev.fieldOfStudyOther,
          additionalSchools: source.additionalSchools?.length
            ? source.additionalSchools
            : prev.additionalSchools,
          selectedCategories: source.selectedCategories?.length
            ? source.selectedCategories
            : prev.selectedCategories,
          categoryDetails:
            source.categoryDetails && Object.keys(source.categoryDetails).length
              ? source.categoryDetails
              : prev.categoryDetails,
          teachingModes: source.teachingModes?.length
            ? source.teachingModes
            : prev.teachingModes,
          teachingModeOther: source.teachingModeOther || prev.teachingModeOther,
          location: source.location?.address ? source.location : prev.location,
          bio: source.bio || prev.bio,
          socialMedia: source.socialMedia?.link
            ? source.socialMedia
            : prev.socialMedia,
        }));
        setCurrentStep(Math.min(source.registrationStep || 1, totalSteps));
        toast("Welcome back — we restored your saved progress.", { icon: "👋" });
        // Don't also show "Draft saved" for the redundant autosave that
        // fires right after this prefill (formData changing triggers the
        // debounced-save effect even though nothing new was typed) — the
        // "Welcome back" toast already told them their progress is safe.
        hasShownDraftToastRef.current = true;
      }
      hasResumedRef.current = true;
    };

    resume();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced autosave — fires ~3s after the user stops typing/changing
  // anything, text fields only (no files, see buildFormData above).
  useEffect(() => {
    if (!hasResumedRef.current) return; // don't race the resume prefill above
    isDirtyRef.current = true;

    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      saveProgress({ includeFiles: false });
    }, 3000);

    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 2 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({ ...prev, picture: file }));
    if (errors.picture) setErrors((prev) => ({ ...prev, picture: "" }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateFile(file, { maxSizeBytes: 5 * 1024 * 1024 });
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: file }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Ensure formData has teachingExperience and teachingExperienceOther fields

  const handleAddSchool1 = () => {
    if (formData.schoolName && formData.degree && formData.fieldOfStudy) {
      const newSchool = {
        id: Date.now(),
        schoolName: formData.schoolName,
        degree: formData.degree,
        fieldOfStudy: formData.fieldOfStudy,
      };
      setFormData({
        ...formData,
        additionalSchools: [...formData.additionalSchools, newSchool],
        schoolName: "",
        degree: "",
        fieldOfStudy: "",
      });
      setErrors({});
    }
  };

  // In your parent component, make sure handleOtherInputChange is defined correctly:
  const handleOtherInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [`${field}Other`]: value,
    }));
  };

  // Generic handleInputChange that handles both regular and other fields
  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({
  //     ...prev,
  //     [name]: value,
  //   }));
  // };

  // Updated handleAddSchool
  const handleAddSchool = () => {
    // Helper to get display value
    const getDisplayValue = (field) => {
      if (formData[field] === "other" && formData[`${field}Other`]) {
        return formData[`${field}Other`];
      }

      // Map value to label for predefined options
      const optionMaps = {
        degree: {
          bsc: "B.Sc (Bachelor's Degree)",
          msc: "M.Sc (Master's Degree)",
          phd: "Ph.D",
          hnd: "HND (Higher National Diploma)",
          ond: "OND (Ordinary National Diploma)",
          nce: "NCE (Nigeria Certificate in Education)",
          pgde: "PGDE (Postgraduate Diploma in Education)",
        },
        fieldOfStudy: {
          "computer-science": "Computer Science",
          mathematics: "Mathematics",
          physics: "Physics",
          chemistry: "Chemistry",
          biology: "Biology",
          english: "English Language",
          economics: "Economics",
          "business-administration": "Business Administration",
          law: "Law",
          engineering: "Engineering",
          medicine: "Medicine",
        },
      };

      return optionMaps[field]?.[formData[field]] || formData[field] || "";
    };

    const newSchool = {
      id: Date.now(),
      schoolName: formData.schoolName,
      degree: getDisplayValue("degree"),
      fieldOfStudy: getDisplayValue("fieldOfStudy"),
    };

    setFormData((prev) => ({
      ...prev,
      additionalSchools: [...(prev.additionalSchools || []), newSchool],
      schoolName: "",
      degree: "",
      degreeOther: "",
      fieldOfStudy: "",
      fieldOfStudyOther: "",
      teachingExperience: "",
      teachingExperienceOther: "",
    }));
  };

  const handleDeleteSchool = (schoolId) => {
    setFormData({
      ...formData,
      additionalSchools: formData.additionalSchools.filter(
        (school) => school.id !== schoolId,
      ),
    });
  };

  const handleAddLicense = () => {
    if (
      formData.licenseName &&
      formData.issuingOrganization &&
      formData.licenseIssuingDate &&
      formData.licenseCredentialId
    ) {
      const newLicense = {
        id: Date.now(),
        name: formData.licenseName,
        organization: formData.issuingOrganization,
        issuingDate: formData.licenseIssuingDate,
        credentialId: formData.licenseCredentialId,
      };
      setFormData({
        ...formData,
        additionalLicenses: [...formData.additionalLicenses, newLicense],
        licenseName: "",
        issuingOrganization: "",
        licenseIssuingDate: "",
        licenseCredentialId: "",
      });
    }
  };

  const handleDeleteLicense = (licenseId) => {
    setFormData({
      ...formData,
      additionalLicenses: formData.additionalLicenses.filter(
        (license) => license.id !== licenseId,
      ),
    });
  };

  const handleInfepToggle = () => {
    setFormData({
      ...formData,
      isInfepRegistered: !formData.isInfepRegistered,
    });
  };

  const handleBecomeInfepMember = () => {
    setFormData({ ...formData, isInfepRegistered: true });
    alert("Redirecting to INFEP registration...");
  };

  // Step 4 handlers
  const handleCourseSelect = (e) => {
    const value = e.target.value;
    if (
      formData.selectedCourses.length < 5 &&
      !formData.selectedCourses.includes(value)
    ) {
      setFormData({
        ...formData,
        selectedCourses: [...formData.selectedCourses, value],
      });
      if (errors.courses) setErrors({ ...errors, courses: "" });
    }
  };

  const handleRemoveCourse = (course) => {
    setFormData({
      ...formData,
      selectedCourses: formData.selectedCourses.filter((c) => c !== course),
    });
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value, // value is the full place object
    }));
  };

  const nextStep = async () => {
    const validationErrors = validateForm(currentStep);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (currentStep < totalSteps) {
      // A completed section is the natural checkpoint — save it (including
      // any files picked on this step) before moving on, not just the
      // debounced text-only autosave. registrationStep records where to
      // RESUME (the next step), not the one just finished.
      isDirtyRef.current = true;
      await saveProgress({ includeFiles: true, step: currentStep + 1 });
      setCurrentStep(currentStep + 1);
    } else {
      setShowCompletionModal(true);
      // console.log("Registration completed:", formData);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // "Skip" used to bypass step 1 entirely, with no validation at all — the
  // one way the 4 compulsory fields (photo, bio, ID, location) could be
  // avoided altogether. It still skips everything else on step 1 (gender,
  // NIN, etc.) — only these 4 now block it.
  //
  // Skip used to just bump currentStep without ever calling saveProgress —
  // whatever was filled in (including the picture File and NIN) was lost the
  // moment the user navigated away, since the debounced autosave explicitly
  // excludes files and may not have fired yet. Now it saves exactly like
  // "Continue" does before advancing.
  const skipStep = async () => {
    if (currentStep !== 1) {
      if (currentStep < totalSteps) {
        isDirtyRef.current = true;
        await saveProgress({ includeFiles: true, step: currentStep + 1 });
        setCurrentStep(currentStep + 1);
      }
      return;
    }

    const requiredErrors = validateCompulsoryBasics();
    if (Object.keys(requiredErrors).length > 0) {
      setErrors(requiredErrors);
      toast.error(
        "Please add your profile photo, bio, ID card, and location before continuing.",
      );
      return;
    }

    isDirtyRef.current = true;
    await saveProgress({ includeFiles: true, step: currentStep + 1 });
    setCurrentStep(currentStep + 1);
  };

  // Previously just an alias for prevStep — it moved the user BACKWARDS and
  // saved nothing at all, despite the label promising otherwise. Now it
  // actually persists whatever's filled in (including files) on the current
  // step, so the resume-on-mount effect above can bring them straight back.
  const handleSaveForLater = async () => {
    isDirtyRef.current = true;
    await saveProgress({ includeFiles: true, step: currentStep });
    toast.success("Progress saved — come back anytime to continue.");
    navigate("/");
  };

  const handleProceedToHome = () => {
    setShowCompletionModal(false);
    const redirectTo = consumePostAuthRedirect();
    navigate(redirectTo || "/tutor-home");
  };

  // Replace the analysis section at the bottom with:
  const getAnalysisConfig = (step) => {
    const stepConfigs = {
      1: {
        title: "Share Your Knowledge: Become a Tutor Today!",
        description:
          "Unlock your potential and share your expertise with students in need! By setting up your account, you'll gain access to a platform that connects you with learners eager to benefit from your knowledge and teaching skills. Get started today and begin inspiring the next generation of thinkers, leaders, and innovators.",
        chartType: "mixed",
        icons: [
          {
            src: "/images/visibility.png",
            alt: "visibility",
            className: "visibility",
            text: "Visibility",
          },
          {
            src: "/images/whatsapp-link.png",
            alt: "whatsapp link",
            className: "whatsapp-link",
            text: "WhatsApp Link",
          },
        ],
      },
      2: {
        title: "Build Credibility with Your Education",
        description:
          "Showcase your academic achievements and teaching qualifications to establish trust with students and parents. A strong educational background positions you as a reliable expert in your field.",
        chartType: "area",
        axisXLabels: ["NCE", "HND", "B.Sc", "M.Sc", "PhD"],
        axisYLabels: ["100", "200", "300", "400", "500"],
        customContent: () => (
          <div className="step2-stats">
            <p>
              <strong>Pro Tip:</strong> Higher qualifications attract premium
              students
            </p>
          </div>
        ),
      },
      3: {
        title: "Professional Certifications Matter",
        description:
          "Add your teaching credentials and licenses to validate your expertise. Certified tutors get 3x more inquiries from serious students and parents looking for qualified educators.",
        chartType: "pie",
        icons: [
          {
            src: "/images/certificate.png",
            alt: "Certificate",
            text: "Certifications",
          },
          { src: "/images/license.png", alt: "License", text: "Licenses" },
        ],
        customContent: () => (
          <div>
            <p>INFEP members get priority placement in our tutor directory</p>
          </div>
        ),
      },
      4: {
        title: "Maximize Reach with Right Courses",
        description:
          "Select courses with high demand to connect with more students. Our analytics show Mathematics, English, and Sciences consistently attract the most learners looking for quality tutors.",
        chartType: "mixed",
        customContent: () => (
          <div className="step4-stats">
            <p>
              <strong>Top Demand:</strong> Mathematics (45%), English (30%),
              Sciences (25%)
            </p>
          </div>
        ),
      },
      5: {
        title: "Complete Your Profile for Maximum Impact",
        description:
          "A complete profile with social links and bio increases your visibility by 70%. Students prefer connecting with tutors who have active professional presence and engaging content.",
        chartType: "pie",
        icons: [
          {
            src: "/images/linkedin.png",
            alt: "LinkedIn",
            text: "Professional Network",
          },
          { src: "/images/social.png", alt: "Social", text: "Social Presence" },
        ],
      },
    };

    return stepConfigs[step] || stepConfigs[1];
  };
  // if (!isLoaded) return <div>Loading...</div>;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handlePictureChange={handlePictureChange}
            // handleOtherInputChange={handleOtherInputChange}
            handleIdChange={handleFileChange}
            registrationType="tutor"
            userName={userName}
          />
        );
      case 2:
        return (
          <Step2
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handleAddSchool={handleAddSchool}
            handleDeleteSchool={handleDeleteSchool}
            handleAddLicense={handleAddLicense}
            handleDeleteLicense={handleDeleteLicense}
            // handleInfepToggle={handleInfepToggle}
            // handleBecomeInfepMember={handleBecomeInfepMember}
            handlePictureChange={handlePictureChange}
            handleLocationChange={handleLocationChange}
            handleCourseSelect={handleCourseSelect}
            handleRemoveCourse={handleRemoveCourse}
            handleOtherInputChange={handleOtherInputChange}
            /* handlers */
          />
        );
      // case 3:
      //   return (
      //     <StepCredentials
      //       formData={formData}
      //       setFormData={setFormData}
      //       errors={errors}
      //       setErrors={setErrors}
      //       handleInputChange={handleInputChange}
      //       handleFileChange={handleFileChange}
      //       handleAddSchool={handleAddSchool}
      //       handleDeleteSchool={handleDeleteSchool}
      //       handleAddLicense={handleAddLicense}
      //       handleDeleteLicense={handleDeleteLicense}
      //       handleInfepToggle={handleInfepToggle}
      //       handleBecomeInfepMember={handleBecomeInfepMember}
      //       handlePictureChange={handlePictureChange}
      //       handleLocationChange={handleLocationChange}
      //       handleCourseSelect={handleCourseSelect}
      //       handleRemoveCourse={handleRemoveCourse} /* handlers */
      //     />
      //   );
      // case 4:
      //   return (
      //     <Step4
      //       formData={formData}
      //       setFormData={setFormData}
      //       errors={errors}
      //       setErrors={setErrors}
      //       handleInputChange={handleInputChange}
      //       handleFileChange={handleFileChange}
      //       handleAddSchool={handleAddSchool}
      //       handleDeleteSchool={handleDeleteSchool}
      //       handleAddLicense={handleAddLicense}
      //       handleDeleteLicense={handleDeleteLicense}
      //       handleInfepToggle={handleInfepToggle}
      //       handleBecomeInfepMember={handleBecomeInfepMember}
      //       handlePictureChange={handlePictureChange}
      //       handleLocationChange={handleLocationChange}
      //       handleCourseSelect={handleCourseSelect}
      //       handleRemoveCourse={handleRemoveCourse} /* handlers */
      //     />
      //   );
      // case 5:
      //   return (
      //     <Step5
      //       formData={formData}
      //       setFormData={setFormData}
      //       errors={errors}
      //       setErrors={setErrors}
      //       handleInputChange={handleInputChange}
      //     />
      //   );
      // default:
      default:
        return null;
    }
  };

  const handleFinalSubmit = async () => {
    const rateWarnings = findUnusualRateWarnings(formData.categoryDetails);
    if (rateWarnings.length > 0) {
      const confirmed = window.confirm(
        `${rateWarnings.join("\n")}\n\nUnusually low rates get flagged for admin review. Submit anyway?`,
      );
      if (!confirmed) return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = buildFormData({ includeFiles: true, includeCompletion: true });

      // Retry transient failures (dropped connection, 5xx) a couple of
      // times before giving up — a flaky mobile connection shouldn't force
      // re-picking every file and re-filling the form. Validation/duplicate
      // errors (4xx) are NOT retried since resubmitting identical data would
      // just fail identically.
      const MAX_ATTEMPTS = 3;
      let res;
      for (let attempt = 1; ; attempt++) {
        try {
          res = await api.put("/users/me", data);
          break;
        } catch (err) {
          const isNetworkError = !err.response;
          const isServerError = err.response?.status >= 500;
          if (!(isNetworkError || isServerError) || attempt >= MAX_ATTEMPTS) {
            throw err;
          }
          toast(`Connection issue — retrying… (${attempt}/${MAX_ATTEMPTS - 1})`, {
            icon: "🔄",
          });
          await new Promise((r) => setTimeout(r, attempt * 1500));
        }
      }

      toast.success("Profile completed successfully!");
      updateUser(res.data.user);
      setShowCompletionModal(true);
    } catch (err) {
      console.error("Tutor submit error:", err);
      const resData = err.response?.data;
      const message = resData?.message || "Failed to save profile";
      // The backend now attaches a TL-PROFILE-### reference for every save
      // failure — surfacing it means a support ticket can go straight to the
      // matching ErrorLog entry instead of "it just didn't work."
      const display = resData?.code ? `${message} (Ref: ${resData.code})` : message;
      toast.error(display, { duration: 6000 });
      setErrors({ submit: display });
    } finally {
      setIsLoading(false);
    }
  };

  // const handleFinalSubmit = async () => {
  //   setIsLoading(true);
  //   setErrors({});

  //   try {
  //     const data = new FormData();

  //     /* ================= PROFILE PICTURE ================= */
  //     if (formData.picture) {
  //       data.append("picture", formData.picture);
  //     }

  //     /* ================= DOCUMENTS (New Structure) ================= */
  //     // Add ID Document as part of documents array
  //     if (formData.idDocument) {
  //       data.append("documents", formData.idDocument);
  //     }

  //     // You can add more document fields later if needed
  //     // Example: if you have licenseDocument, etc. → data.append("documents", file)

  //     /* ================= TEXT FIELDS ================= */
  //     data.append("gender", formData.gender);
  //     // data.append("phone", formData.phone || "");
  //     if (formData.phone?.trim()) {
  //       data.append("phone", formData.phone.trim());
  //     }

  //     // Education
  //     data.append("schoolName", formData.schoolName);
  //     data.append("degree", formData.degree);
  //     if (formData.degree === "other")
  //       data.append("degreeOther", formData.degreeOther);
  //     data.append("fieldOfStudy", formData.fieldOfStudy);
  //     if (formData.fieldOfStudy === "other")
  //       data.append("fieldOfStudyOther", formData.fieldOfStudyOther);

  //     // Teaching
  //     data.append("programType", formData.programType);
  //     data.append("mainProgram", formData.mainProgram);
  //     data.append("ratePeriod", formData.ratePeriod);
  //     data.append("hourlyRate", Number(formData.hourlyRate) || 0);
  //     data.append("currency", formData.currency);

  //     // Courses
  //     formData.selectedCourses.forEach((course, index) => {
  //       data.append(`selectedCourses[${index}]`, course);
  //     });

  //     // Location
  //     data.append("location[address]", formData.location.address || "");
  //     data.append("location[placeId]", formData.location.placeId || "");
  //     data.append("location[lat]", formData.location.lat || 0);
  //     data.append("location[lng]", formData.location.lng || 0);

  //     // Bio & Social
  //     data.append("bio", formData.bio || "");
  //     if (formData.socialMedia.platform && formData.socialMedia.link) {
  //       data.append("socialMedia", JSON.stringify(formData.socialMedia));
  //     }

  //     // Arrays
  //     if (formData.additionalSchools?.length > 0) {
  //       data.append(
  //         "additionalSchools",
  //         JSON.stringify(formData.additionalSchools),
  //       );
  //     }
  //     if (formData.additionalLicenses?.length > 0) {
  //       data.append(
  //         "additionalLicenses",
  //         JSON.stringify(formData.additionalLicenses),
  //       );
  //     }

  //     /* ================= FINAL FLAGS ================= */
  //     data.append("profileCompleted", "true");
  //     data.append("role", "tutor"); // Important!

  //     // console.log("Submitting Tutor FormData:");
  //     for (let pair of data.entries()) {
  //       // console.log(pair[0], pair[1]);
  //     }

  //     const res = await api.put("/users/me", data);

  //     toast.success("Profile completed successfully!");
  //     updateUser(res.data.user);
  //     setShowCompletionModal(true);
  //   } catch (err) {
  //     console.error("Tutor submit error:", err);
  //     toast.error(err.response?.data?.message || "Failed to save profile");
  //     setErrors({
  //       submit: err.response?.data?.message || "Failed to save profile",
  //     });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  return (
    <div className="container">
      <div className="registration-section">
        <div className="logo-container">
          <img
            src="../images/tutors-logo.png"
            alt="Website Logo"
            className="logo"
          />
        </div>
        <div className="registration-form-container">
          <div className="progress-bar">
            {Array.from({ length: totalSteps }, (_, index) => (
              <span
                key={index + 1}
                className={`progress-line ${
                  index + 1 <= currentStep ? "active-line" : ""
                }`}
              ></span>
            ))}
          </div>

          {saveStatus !== "idle" && (
            <div className={`autosave-status autosave-status--${saveStatus}`}>
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && "✓ All changes saved"}
              {saveStatus === "error" && "⚠ Couldn't save — will retry"}
            </div>
          )}

          {currentStep > 1 && (
            <div className="back-container">
              <button onClick={prevStep} className="tutor-back-btn">
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>
          )}

          {currentStep === 1 && (
            <h1 className="title">
              Welcome, {userName}. Let's set you up and get you ready!
            </h1>
          )}

          <div className="tutor-form-section">
            <form
              // onSubmit={(e) => {
              //   e.preventDefault();
              //   nextStep();
              // }}

              onSubmit={(e) => {
                e.preventDefault();
                if (currentStep !== totalSteps) {
                  nextStep();
                  return;
                }

                // Last-line guard — reaching step 2 via "Continue" or "Skip"
                // already enforces these 4, but final submission is the one
                // point nothing should be able to get past regardless of how
                // the user arrived here (stale tab, browser back/forward).
                const requiredErrors = validateCompulsoryBasics();
                if (Object.keys(requiredErrors).length > 0) {
                  setErrors(requiredErrors);
                  toast.error(
                    "Please add your profile photo, bio, ID card, and location before submitting.",
                  );
                  setCurrentStep(1); // these fields all live on step 1
                  return;
                }

                handleFinalSubmit();
              }}
            >
              {renderStep()}
              <div className="button-container">
                {currentStep === 1 ? (
                  <button type="button" onClick={skipStep} className="skip-btn">
                    Skip
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSaveForLater}
                    className="save-later-btn"
                  >
                    Save and Continue Later
                  </button>
                )}
                <button
                  type="submit"
                  className="continue-btn"
                  disabled={isLoading}
                >
                  {/* {currentStep === totalSteps ? "Submit" : "Continue"}{" "} */}
                  {isLoading
                    ? "Saving..."
                    : currentStep === totalSteps
                      ? "Complete Registration"
                      : "Continue"}
                  <span className="arrow">→</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal - unchanged */}
      {showCompletionModal && (
        <SuccessModal
          show={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          onProceed={handleProceedToHome}
          title="Welcome to TutorsLinks Community🤗"
          subtitle="Breathe easy and unwind! We've got your details, and our team is
                 on it. You'll be up and running before you know you it. Thanks
               for hanging in there!."
        />
      )}
      {/* // Replace with: */}
      {currentStep === 1 && <AnalysisSection1 />}
      {currentStep === 2 && <AnalysisSection2 />}
      {/* {currentStep === 3 && <AnalysisSection3 />}
      {currentStep === 4 && <AnalysisSection4 />}
      {currentStep === 5 && <AnalysisSection5 />} */}

      {/* // Or if you want it to show conditionally for step 1 only: */}
      {/* {renderStep()}
      {currentStep === 1 && <AnalysisSection1 />} */}
    </div>
  );
};

export default TutorRegistration;
